// Sentry init helper for the React client.
//
// Graceful no-op when SENTRY_CLIENT_DSN is unset (matches the server pattern).
// Idempotent: calling initSentry() more than once does not re-init the SDK.
//
// SENTRY_CLIENT_DSN does not use the VITE_ prefix because we follow the
// SENTRY_CLIENT_* / SENTRY_SERVER_* naming scheme. vite.config.ts injects it
// into `import.meta.env.SENTRY_CLIENT_DSN` via `define` so it still reaches
// the browser bundle.
//
// Environment tag uses Vite's `import.meta.env.MODE` ("development" or
// "production"), which has the same semantics as Node's NODE_ENV here.
//
// Release tag uses VITE_APP_VERSION, injected at build time by vite.config.ts
// from package.json. This matches the server `release` so events from both
// runtimes correlate in Sentry.

import * as Sentry from "@sentry/react";

let initialized = false;

export function initSentry(): void {
  if (initialized) return;

  const dsn = import.meta.env.SENTRY_CLIENT_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,
    tracesSampleRate: 0,
    // CSR-1989 — a user reported search returning "no insureds found" after an
    // idle tab, and nothing was recorded: no replay here, and AMS has no Datadog
    // RUM application. Replay is captured only for sessions that hit an error, so
    // the next occurrence arrives with the failure visible instead of a
    // second-hand description. Healthy sessions record nothing.
    //
    // Masking is mandatory, not tuning: these screens show insured names, DOT
    // numbers, emails, phone numbers and uploaded documents. Text, inputs and
    // media are all masked or blocked, so a replay shows layout and interaction
    // without shipping customer PII to Sentry.
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });

  initialized = true;
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
